// v0.6 — canonical-row dedup for the grouped review queue.
//
// PRESENTATION ONLY. Resolution stays per (transaction, flag_type) and the
// freeze gate (_review_gate.py) is untouched — a transaction that carries N
// flags is still rendered once per flag CARD (the cards are real information
// architecture), but its ROW is rendered in full (context + actions) in only
// ONE card and cross-referenced from the others.
//
// Two invariants baked in here:
//   1. STATELESS / recomputed every render from the CURRENTLY-LOADED rows.
//      When a flag resolves and drops off the transaction, the next-highest
//      card automatically becomes canonical on the next render — no cached
//      "promote this card" step.
//   2. Canonical is chosen ONLY among loaded rows (the map is built from the
//      groups' `txn_ids` the payload actually returned), so a compact
//      reference can never point at a row/action block that isn't rendered.
//
// Tie-breaker priority (operator-approved): the most actionable/specific flag
// wins — specialised resolvers first, then per-row Resolve (Family B), then
// bulk-ack / informational (Family A). FxTreatmentAmend is only ever a
// sub-control inside the Family-B PerRowResolver, so it is covered by the
// Family-B rank rather than a separate tier. Ties fall back to the existing
// card order.

import { fmtMoney } from "../../lib/format";
import { answerRank } from "./answers";

export const HAS_PER_ROW_ACTION_MIN = 50;

// v0.6.6 — the backend sums total_gross across EVERY row in a group
// regardless of currency (api/_review_grouped_builder.py), and `currency`
// is only the first row's — a group.currencies with >1 entry (e.g. a
// MISSING_EXCHANGE_RATE group spanning EUR + USD rows) means total_gross
// is a meaningless cross-currency blend mislabeled with one of the
// currencies. Pulled out here (not left inline in ReviewGroupCard.jsx) so
// it's independently testable without dragging in that component's full
// dependency tree (auth, api, shadcn Sheet primitives) — same reason
// rowActionRank/buildCanonicalMap live in this file.
export const bulkAckTotal = (group) => {
  const currencies = group.currencies || [];
  if (currencies.length > 1) {
    return `amounts across ${currencies.length} currencies: ${currencies.join(", ")}`;
  }
  return `${fmtMoney(group.total_gross)} ${group.currency || ""}`.trim();
};

// A2-582 — how much a card's rows can be answered ON it, from the answers
// the server sent (answers.js answerRank): a card whose rows take an answer
// that changes them (a number, a country, a channel...) ranks highest, then a
// rate pick, then a blocking card's "keep as it is", then a note. This was a
// hand list of flag names (v0.6.12 A1-60..A2-196), and every new control had
// to be added to it or its rows folded away onto a card with no way out.
export const rowActionRank = (g) => answerRank(g);   // A1-103: ranks what the card draws

export const buildCanonicalMap = (groups = []) => {
  const occ = {};
  const best = {};
  groups.forEach((g, order) => {
    const rank = rowActionRank(g, groups);
    (g.txn_ids || []).forEach((tid) => {
      occ[tid] = (occ[tid] || 0) + 1;
      const cur = best[tid];
      if (!cur || rank > cur.rank || (rank === cur.rank && order < cur.order)) {
        best[tid] = { flag_type: g.flag_type, rank, order };
      }
    });
  });
  return {
    canonicalOf: (tid) => best[tid] || null,
    countOf: (tid) => occ[tid] || 0,
  };
};
